import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, SoccerBall } from "@phosphor-icons/react";
import { ageGroupFromDob } from "../lib/ageGroup";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = ["Goalkeeper", "Defender", "Midfielder", "Forward", "Winger", "Striker"];
const FEET = ["Right", "Left", "Both"];

const inputCls = "w-full h-12 px-3 bg-[#121212] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429] text-white placeholder:text-neutral-600";

function formatDateInput(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  if (digits.length <= 4) return y;
  if (digits.length <= 6) return `${y}-${m}`;
  return `${y}-${m}-${d}`;
}

export default function Register() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", date_of_birth: "", position: "", preferred_foot: "Right",
    team: "", age_group: "",
    parent_name: "", parent_email: "", parent_phone: "", notes: "",
  });
  const [settings, setSettings] = useState({ teams: [], age_groups: [] });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/settings`).then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/register/player`, form);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit registration");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center py-8 space-y-4" data-testid="register-success">
          <CheckCircle size={64} weight="fill" color="#06D6A0" className="mx-auto" />
          <h1 className="font-display font-black text-3xl uppercase tracking-tight">You're on the list</h1>
          <p className="text-neutral-400 text-sm max-w-sm mx-auto">
            Thanks for signing {form.first_name || "your child"} up. The coach will review the registration and reach out to <span className="text-white font-semibold">{form.parent_email}</span> shortly.
          </p>
          <Link to="/login" className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-sm">
            Back to Sign In <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <div className="font-display font-black text-2xl uppercase tracking-tight">Register your child</div>
          <div className="text-sm text-neutral-500 mt-1">Tell us a bit about the player. A coach will review and get in touch.</div>
        </div>

        <SectionTitle>Player</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *">
            <input required data-testid="reg-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last name *">
            <input required data-testid="reg-last-name" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
          <Field label="Date of birth">
            <input inputMode="numeric" maxLength={10} placeholder="YYYY-MM-DD" data-testid="reg-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", formatDateInput(e.target.value))} />
          </Field>
          <Field label="Preferred foot">
            <select className={inputCls} value={form.preferred_foot} onChange={(e) => set("preferred_foot", e.target.value)}>
              {FEET.map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Position">
            <select data-testid="reg-position" className={inputCls} value={form.position} onChange={(e) => set("position", e.target.value)}>
              <option value="">—</option>
              {POSITIONS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Age group (auto)">
            <div data-testid="reg-age-group" className={`${inputCls} flex items-center font-bold text-[#FFD166]`}>
              {ageGroupFromDob(form.date_of_birth) || "From date of birth"}
            </div>
          </Field>
          <div className="col-span-2">
            <Field label="Team preference (optional)">
              <select className={inputCls} value={form.team} onChange={(e) => set("team", e.target.value)}>
                <option value="">—</option>
                {(settings.teams || []).map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <SectionTitle>Parent / Guardian</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Your name *">
            <input required data-testid="reg-parent-name" className={inputCls} value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} />
          </Field>
          <Field label="Your email *">
            <input required type="email" data-testid="reg-parent-email" className={inputCls} value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} placeholder="you@example.com" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Phone">
              <input data-testid="reg-parent-phone" className={inputCls} value={form.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Anything else the coach should know?">
              <textarea rows={3} data-testid="reg-notes" className="w-full p-3 bg-[#121212] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429] text-white placeholder:text-neutral-600" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. plays for another club, medical notes, previous experience…" />
            </Field>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="reg-submit"
          className="w-full h-14 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-lg flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? "Sending…" : (<>Submit Registration <ArrowRight size={18} weight="bold" /></>)}
        </button>

        <div className="text-center text-xs text-neutral-500">
          Already have an account? <Link to="/login" className="text-[#D90429] font-bold uppercase tracking-wider">Sign in</Link>
        </div>
      </form>
    </Shell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

function SectionTitle({ children }) {
  return <div className="overline pt-2 border-t border-neutral-900">{children}</div>;
}

function Shell({ children }) {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#050505] px-4 py-10"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 80% -10%, rgba(217,4,41,0.18), transparent 60%), radial-gradient(900px 500px at 0% 100%, rgba(6,214,160,0.10), transparent 60%)",
      }}
    >
      <div className="relative w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-md bg-[#D90429] flex items-center justify-center mx-auto">
            <SoccerBall size={30} weight="fill" color="#fff" />
          </div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-3">Wahdat FC</h1>
          <div className="overline mt-1 text-neutral-500">Academy Registration</div>
        </div>
        <div className="bg-[#0a0a0a] border border-neutral-900 rounded-lg p-6 sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
